import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';

const HeroSection = ({ onLoginClick }) => {
  const { t, i18n } = useTranslation();
  const [showLangMenu, setShowLangMenu] = useState(false);
  const langMenuRef = useRef(null);

  const languages = [
    { code: 'it', name: 'Italiano', flag: '🇮🇹' },
    { code: 'bs', name: 'Bosanski', flag: '🇧🇦' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'en', name: 'English', flag: '🇬🇧' }
  ];

  // 1. AUTOMATSKA DETEKCIJA JEZIKA PREKO IP ADRESE
  useEffect(() => {
    const detectLocation = async () => {
      const hasSetLanguageManual = localStorage.getItem('i18nextLngManual');
      if (hasSetLanguageManual) return;

      try {
        const response = await fetch('https://ipapi.co/json/');
        if (!response.ok) throw new Error('Mreža nije dostupna');
        
        const data = await response.json();
        if (!data || !data.country_code) return;

        const country = data.country_code;
        let detectedLang = 'en';

        if (country === 'IT') {
          detectedLang = 'it';
        } else if (['BA', 'RS', 'HR', 'ME'].includes(country)) {
          detectedLang = 'bs';
        } else if (['DE', 'AT', 'CH'].includes(country)) {
          detectedLang = 'de';
        } else {
          detectedLang = 'en';
        }

        if (i18n.language !== detectedLang) {
          i18n.changeLanguage(detectedLang);
        }
      } catch (error) {
        console.error("Automatska detekcija lokacije nije uspjela:", error);
      }
    };

    detectLocation();
  }, [i18n]);

  const currentLang = languages.find(l => l.code === i18n.language) || languages[0];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (langMenuRef.current && !langMenuRef.current.contains(event.target)) {
        setShowLangMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const changeLanguage = (code) => {
    i18n.changeLanguage(code);
    localStorage.setItem('i18nextLngManual', 'true'); 
    setShowLangMenu(false);
  };

  return (
    <section className="w-full min-h-[80vh] flex items-center justify-center px-4 md:px-6 lg:px-8 py-12 md:py-16 lg:py-20">
      <div className="max-w-4xl mx-auto text-center space-y-6 md:space-y-8 lg:space-y-10">
        
        {/* Logotip: Štit sa slovom O i Desk ispod */}
        <div className="flex flex-col items-center justify-center mb-6 md:mb-8 lg:mb-10 animate-fade-in">
          <div className="relative flex items-center justify-center">
            <Icon 
              name="Shield" 
              size={56} 
              color="var(--color-primary)" 
              className="w-14 h-14 md:w-16 md:h-16 lg:w-20 lg:h-20" 
              strokeWidth={1.6}  /* PROMIJENJENO SA 2.5 NA 2 ZA ORIGINALNI IZGLED */
            />
            <span className="absolute inset-0 flex items-center justify-center text-primary font-black text-2xl md:text-3xl lg:text-4xl select-none" style={{ transform: 'translateY(-5%)' }}>
              O
            </span>
          </div>
          <span className="text-primary font-bold uppercase tracking-widest text-xs md:text-sm mt-2 select-none">
            Desk
          </span>
        </div>

        {/* Tekstualni dio */}
        <div className="space-y-4 md:space-y-5 lg:space-y-6">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-foreground leading-tight tracking-tight">
            {t('hero.welcome')} <span className="text-primary">OrizonDesk</span>
          </h1>
      
        </div>

        {/* Dugme za Login */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-6 md:pt-8 lg:pt-10">
          <button
            onClick={onLoginClick}
            className="w-full sm:w-auto min-w-[260px] flex items-center justify-center gap-3 px-10 py-5 bg-primary text-primary-foreground rounded-2xl hover:bg-primary/90 transition-all duration-300 shadow-elevation-2 hover:shadow-elevation-3 hover:scale-[1.02] active:scale-[0.98]"
          >
            <Icon name="LogIn" size={24} color="currentColor" />
            <span className="text-xl font-bold">{t('hero.cta_button')}</span>
          </button>
        </div>

        {/* GDPR i Selekcija jezika */}
        <div className="flex flex-col items-center gap-6 pt-8">
          <p className="text-sm text-muted-foreground animate-pulse">
            {t('hero.gdpr_note')}
          </p>

          <div className="relative" ref={langMenuRef}>
            <button 
              type="button"
              onClick={() => setShowLangMenu(!showLangMenu)}
              className="flex items-center gap-2 px-4 py-2 bg-card border border-border rounded-xl hover:bg-muted transition-all shadow-sm"
            >
              <span className="text-xl">{currentLang.flag}</span>
              <span className="text-sm font-medium">{currentLang.name}</span>
              <Icon name="ChevronDown" size={14} className={`transition-transform ${showLangMenu ? 'rotate-180' : ''}`} />
            </button>

            {showLangMenu && (
              <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-white rounded-xl shadow-2xl border border-gray-200 z-[1000] min-w-[180px] py-2 overflow-hidden animate-in fade-in slide-in-from-bottom-2">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => changeLanguage(lang.code)}
                    className={`w-full flex items-center gap-3 px-5 py-3 hover:bg-gray-100 transition-colors ${i18n.language === lang.code ? 'bg-primary/5 text-primary font-bold' : 'text-gray-700'}`}
                  >
                    <span className="text-xl">{lang.flag}</span>
                    <span className="text-sm">{lang.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

      </div>
    </section>
  );
};

export default HeroSection;